import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { NetworkRequest } from "../../../common/models";
import type { SetupTask } from "../../components/organisms/tasks/TaskCard";
import { usePrimaryOrganization } from "../../hooks";
import { useSelfOrganizationUser } from "../../hooks/useOrganization";
import { Routes } from "../../root/routes";
import { useBusinessProfileAndVendorInventoryStatus } from "../data-map/DataMapDashboardPage";
import { useOrgRegulationRequestTypes } from "../../hooks/useOrgRegulationRequestTypes";
import { useWorkflowSetupNavItems } from "../requestInstructions/WorkflowSetupVerticalNav";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";
import {
  DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  DevInstructionsHrNoticeSlug,
  DevInstructionsSurveySlug,
  getDevInstructionsCompletionStatus,
  usePrivacyNoticeChecklistItems,
} from "../stay-compliant/PrivacyNoticeChecklistItems";
import {
  DevInstructionsDSARFormsOnboardingSlug,
  DevInstructionsPoliciesAndNoticesOnboardingSlug,
} from "../stay-compliant/privacy-center/DSARForms";
import { useNonEmploymentCollectionGroups } from "../../hooks/useCollectionGroups";
import {
  DevInstructionsFinishedApiIntegrationsSetupSlug,
  DevInstructionsFinishedDataStorageSettingsSetupSlug,
  DevInstructionsFinishedWorkflowSetupSlug,
  getWorkflowSetupRoute,
  isWorkflowSetupComplete,
} from "../stay-compliant/WorkflowsDashboardPage";
import { getAllInformationForLimitRequests } from "../requestInstructions/LimitInstructionsPage";
import {
  isWebsiteConsentSetupComplete,
  useConsentBannerSettings,
} from "../../components/superorg/settings/ConsentBannerSettings";
import { useEditPrivacyCenter } from "../../components/privacyCenter/EditPrivacyCenter";
import { usePrivacyCenterSettings } from "../../components/superorg/settings/PrivacyCenterSettings";
import { usePrivacyCenterNotice } from "../../hooks/usePrivacyCenterNotice";
import { useDataRetentionModule } from "../../components/organisms/data-retention/DataRetentionModule";
import { isDataRetentionComplete } from "../data-map/StayCompliantDataRetentionPage";
import { useConsumerDataFlowsCompletion } from "../data-map/data-flows/DataFlowsDashboardPage";
import { useOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";
import { DevInstructionsFinishedHrDsarWorkflowsSlug } from "../stay-compliant/hr-center/HRCenterDashboardPage";
import { useVisibleFlaggedBusinessProfileQuestions } from "../../hooks/useVisibleFlaggedBusinessProfileQuestions";

const setupTask = (
  id: string,
  name: string,
  description: string,
  callToActionUrl: string,
): SetupTask => ({
  id,
  name,
  description,
  callToAction: "Finish Set Up",
  callToActionUrl,
});

export function useDashboardSetupTasks(): SetupTask[] | undefined {
  const [organization] = usePrimaryOrganization();
  const currentOrgUser = useSelfOrganizationUser();
  const { filterByWorkflowType, loading: requestTypesLoading } = useOrgRegulationRequestTypes();
  const { allVendors, isSurveyComplete, isVendorInventoryFinished, isVendorDocumentationFinished } =
    useBusinessProfileAndVendorInventoryStatus();
  const hasFlaggedBusinessProfileQuestions = useVisibleFlaggedBusinessProfileQuestions();
  const { answers: devInstructionsAnswers, request: devInstructionsRequest } = useSurvey(
    organization?.id,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );
  const [allCollectionGroups, collectionGroupsRequest] = useNonEmploymentCollectionGroups(
    organization?.id,
  );
  // useCollectionGroups hands back [] while loading, which would read as "no groups".
  const collectionGroups = NetworkRequest.areFinished(collectionGroupsRequest)
    ? allCollectionGroups
    : undefined;
  // useDataMap hands back an empty map while loading, so gate on the request instead.
  const { data: dataMap, request: dataMapRequest } = useDataMap(organization?.id);
  const isDataMapLoaded = NetworkRequest.areFinished(dataMapRequest);
  const { checklistProps, ready: privacyNoticesReady } = usePrivacyNoticeChecklistItems(
    organization?.id,
  );
  const {
    settings: cmpSettings,
    privacyCenterRequest,
    cmpCustomizationRequest,
  } = useConsentBannerSettings();
  const editPrivacyCenter = useEditPrivacyCenter();
  const { privacyCenter } = usePrivacyCenterSettings();
  const [draftNotice, draftNoticeRequest] = usePrivacyCenterNotice(
    organization?.id,
    privacyCenter?.id,
    editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId,
  );
  const dataRetentionModule = useDataRetentionModule();
  const dataFlowsCompletion = useConsumerDataFlowsCompletion(collectionGroups);
  const [onboardingProgress, onboardingProgressRequest] = useOnboardingProgress(organization?.id);

  const isFinishedWithApiIntegrationsSetup =
    devInstructionsAnswers?.[DevInstructionsFinishedApiIntegrationsSetupSlug] === "true";
  const isFinishedDataStorageOnboarding =
    devInstructionsAnswers?.[DevInstructionsFinishedDataStorageSettingsSetupSlug] === "true";
  const isFinishedWorkflowSetupOnboarding =
    devInstructionsAnswers?.[DevInstructionsFinishedWorkflowSetupSlug] === "true";
  const isLimitSettingsComplete =
    isDataMapLoaded && allVendors
      ? getAllInformationForLimitRequests(allVendors, dataMap).hasAllRequiredResponses
      : false;
  const workflowNavItems = useWorkflowSetupNavItems({
    selectedTabKey: "consumer-lookup",
    hasRequestTypesForWorkflow: (workflowType) =>
      requestTypesLoading ? true : filterByWorkflowType(workflowType as WorkflowType).length > 0,
    isLimitStepIncomplete: isFinishedWorkflowSetupOnboarding && !isLimitSettingsComplete,
    isFinishedWorkflowSetupOnboarding,
  });

  if (
    !organization ||
    !currentOrgUser ||
    !devInstructionsAnswers ||
    !NetworkRequest.areFinished(devInstructionsRequest)
  ) {
    return undefined;
  }

  // Treat the one-time Workflow Setup completion as the proxy for whether the
  // organization has finished onboarding.
  if (!isFinishedWorkflowSetupOnboarding) {
    return [];
  }

  const isHrUser = currentOrgUser.role === "HR";
  const shouldLoadHrStatus =
    organization.requiresEmployeeMapping && currentOrgUser.role !== "GENERAL";

  // Build the list only once every input it reads has loaded. Returning a partial list made
  // cards appear late, or appear and then vanish when a loading default was read as an answer.
  const isPrivacyCenterLoaded = NetworkRequest.areFinished(privacyCenterRequest);
  const draftNoticeId = editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId;
  const dataRetentionRequests = dataRetentionModule.requests;
  const areConsumerInputsReady =
    !requestTypesLoading &&
    isDataMapLoaded &&
    allVendors !== undefined &&
    // isSurveyComplete is only computed for an org with a privacy center.
    (isSurveyComplete !== null || (isPrivacyCenterLoaded && !privacyCenter)) &&
    isVendorInventoryFinished !== null &&
    isVendorDocumentationFinished !== null &&
    hasFlaggedBusinessProfileQuestions !== undefined &&
    privacyNoticesReady &&
    isPrivacyCenterLoaded &&
    (!privacyCenter || NetworkRequest.areFinished(cmpCustomizationRequest)) &&
    collectionGroups !== undefined &&
    dataFlowsCompletion.isReady &&
    (!draftNoticeId || NetworkRequest.areFinished(draftNoticeRequest)) &&
    NetworkRequest.areFinished(
      dataRetentionRequests.request,
      dataRetentionRequests.dataMapRequest,
      dataRetentionRequests.dataRetentionRequest,
    ) &&
    (dataRetentionModule.isReady ||
      NetworkRequest.anyFailed(dataRetentionRequests.dataRetentionRequest));
  const areHrInputsReady = NetworkRequest.areFinished(onboardingProgressRequest);

  if ((!isHrUser && !areConsumerInputsReady) || (shouldLoadHrStatus && !areHrInputsReady)) {
    return undefined;
  }

  const tasks: SetupTask[] = [];

  if (!isHrUser) {
    if (!isFinishedWithApiIntegrationsSetup) {
      tasks.push(
        setupTask(
          "workflows-api-integrations",
          "API Integrations",
          "Connect your vendors to TrueVault",
          Routes.privacyCenter.integrations,
        ),
      );
    }
    if (!isFinishedDataStorageOnboarding) {
      tasks.push(
        setupTask(
          "workflows-data-storage",
          "Data Storage",
          "Set up your vendors data access settings",
          Routes.workflows.dataStorageSettings,
        ),
      );
    }

    if (!requestTypesLoading && isDataMapLoaded && allVendors) {
      const workflowSetupComplete = isWorkflowSetupComplete(
        workflowNavItems,
        isFinishedWorkflowSetupOnboarding,
      );
      const workflowSetupRoute = getWorkflowSetupRoute(
        workflowNavItems,
        isFinishedWorkflowSetupOnboarding,
        isLimitSettingsComplete,
      );

      if (!workflowSetupComplete) {
        tasks.push(
          setupTask(
            "workflows-workflow-setup",
            "Workflow Setup",
            "Automate your request processing",
            workflowSetupRoute,
          ),
        );
      }
    }
  }

  if (!isHrUser) {
    const isDSARFormsOnboardingComplete =
      devInstructionsAnswers[DevInstructionsDSARFormsOnboardingSlug] === "true";
    const isPoliciesAndNoticesComplete =
      devInstructionsAnswers[DevInstructionsPoliciesAndNoticesOnboardingSlug] === "true";
    const isDeveloperInstructionsComplete =
      privacyNoticesReady && checklistProps?.notices && allVendors
        ? devInstructionsAnswers[DevInstructionsSurveySlug] === "true" &&
          getDevInstructionsCompletionStatus(
            devInstructionsAnswers,
            checklistProps.notices,
            allVendors,
          ).didFinishConsentManagementInstructions
        : undefined;
    const isWebsiteConsentComplete = NetworkRequest.areFinished(
      privacyCenterRequest,
      cmpCustomizationRequest,
    )
      ? isWebsiteConsentSetupComplete(cmpSettings, organization)
      : undefined;

    if (currentOrgUser.role !== "GENERAL" && !isPoliciesAndNoticesComplete) {
      tasks.push(
        setupTask(
          "privacy-center-policies-notices",
          "Policies & Notices",
          "Review & edit your privacy policy",
          Routes.privacyCenter.privacyPolicy,
        ),
      );
    }
    if (!isDSARFormsOnboardingComplete) {
      tasks.push(
        setupTask(
          "privacy-center-request-forms",
          "Request Forms",
          "Customize your privacy request forms",
          Routes.privacyCenter.dsarForms,
        ),
      );
    }
    if (isDeveloperInstructionsComplete === false) {
      tasks.push(
        setupTask(
          "privacy-center-developer-instructions",
          "Developer Instructions",
          "View steps for publishing your Privacy Center",
          Routes.privacyCenter.publish,
        ),
      );
    }
    if (isWebsiteConsentComplete === false) {
      tasks.push(
        setupTask(
          "privacy-center-website-consent",
          "Website Consent",
          "Configure your website's consent banner",
          Routes.privacyCenter.consentBanner,
        ),
      );
    }

    const isDataFlowsComplete = !collectionGroups
      ? undefined
      : dataFlowsCompletion.isReady
      ? dataFlowsCompletion.isComplete
      : undefined;
    const isVendorDocumentsComplete = !collectionGroups
      ? undefined
      : isVendorDocumentationFinished !== null && allVendors
      ? isVendorDocumentationFinished && allVendors.length > 1
      : undefined;
    const isDataRetentionReady =
      Boolean(draftNotice) &&
      Boolean(editPrivacyCenter) &&
      dataRetentionModule.isReady &&
      NetworkRequest.areFinished(
        draftNoticeRequest,
        dataRetentionRequests.request,
        dataRetentionRequests.dataMapRequest,
        dataRetentionRequests.dataRetentionRequest,
      );
    const isDataRetentionFinished = !collectionGroups
      ? undefined
      : isDataRetentionReady
      ? isDataRetentionComplete(draftNotice, editPrivacyCenter, dataRetentionModule)
      : undefined;

    if (isSurveyComplete === false || hasFlaggedBusinessProfileQuestions) {
      const businessProfileTaskDescription =
        isSurveyComplete === false
          ? hasFlaggedBusinessProfileQuestions
            ? "Review incomplete and flagged questions in your Business Profile"
            : "Answer questions about your business"
          : "Review flagged questions in your Business Profile";

      tasks.push(
        setupTask(
          "data-map-business-profile",
          "Business Profile",
          businessProfileTaskDescription,
          Routes.organization.settings.businessSurvey,
        ),
      );
    }
    if (isVendorInventoryFinished === false) {
      tasks.push(
        setupTask(
          "data-map-vendor-inventory",
          "Vendor List",
          "Add your vendors and data disclosures",
          Routes.dataMap.vendorInventory,
        ),
      );
    }
    if (isDataFlowsComplete === false) {
      tasks.push(
        setupTask(
          "data-map-data-flows",
          "Data Flows",
          "Configure your data flows",
          Routes.dataMap.dataFlows.dashboard,
        ),
      );
    }
    if (isVendorDocumentsComplete === false) {
      tasks.push(
        setupTask(
          "data-map-vendor-documents",
          "Vendor Documents",
          "Review your vendors classification and documentation",
          Routes.dataMap.vendorDocumentation,
        ),
      );
    }
    if (isDataRetentionFinished === false) {
      tasks.push(
        setupTask(
          "data-map-data-retention",
          "Data Retention",
          "Configure your data retention policy",
          Routes.dataMap.dataRetention,
        ),
      );
    }
  }

  if (shouldLoadHrStatus) {
    const isHrNoticesPublished = devInstructionsAnswers[DevInstructionsHrNoticeSlug] === "true";
    const isHrDataMapTaskComplete = onboardingProgress
      ? onboardingProgress.hrCenterOnboardingProgress.isAllHrDataMapCompleted &&
        onboardingProgress.hrCenterOnboardingProgress.isHrDataFlowsFinished
      : undefined;
    const isFinishedWithHrDsarWorkflows =
      devInstructionsAnswers[DevInstructionsFinishedHrDsarWorkflowsSlug] === "true" ||
      isHrNoticesPublished;

    if (isHrDataMapTaskComplete === false) {
      tasks.push(
        setupTask(
          "hr-center-data-map",
          "HR Data Map",
          "Review HR Vendors, collection groups and disclosures",
          Routes.hrCenter.hrDataMap.root,
        ),
      );
    }
    if (!isFinishedWithHrDsarWorkflows) {
      tasks.push(
        setupTask(
          "hr-center-dsar-workflows",
          "HR DSAR Workflows",
          "Review your request workflow settings",
          Routes.hrCenter.hrDsarWorkflows,
        ),
      );
    }
    // Only check publication here; an incomplete Colorado biometric notice has its own task.
    if (!isHrNoticesPublished) {
      tasks.push(
        setupTask(
          "hr-center-notices",
          "Notices",
          "Review your HR Notices and where to post them",
          Routes.hrCenter.hrNotices,
        ),
      );
    }
  }

  return tasks;
}
